import { useState, useEffect, useMemo } from 'react';

interface PagedResult<T> {
  data: T[];
  total: number;
  hasMore: boolean;
}

/**
 * Accumulates paginated API results into a flat list across page increments.
 * Resets automatically when resetKey changes (e.g. a search query or filter).
 *
 * hasMore is preserved from the last settled result rather than going false
 * while the next page is loading — this prevents the Load More button from
 * disappearing and reappearing mid-fetch (layout-shift flicker).
 */
export function usePageAccumulation<T>(
  result: PagedResult<T> | undefined,
  page: number,
  resetKey: string,
): { items: T[]; total: number; hasMore: boolean } {
  const [pages, setPages] = useState<Map<number, T[]>>(new Map());
  const [lastHasMore, setLastHasMore] = useState(false);

  useEffect(() => {
    setPages(new Map());
    setLastHasMore(false);
  }, [resetKey]);

  useEffect(() => {
    if (!result) return;
    setPages((prev) => new Map(prev).set(page, result.data));
    setLastHasMore(result.hasMore);
  }, [result, page]);

  const items = useMemo(() => [...pages.values()].flat(), [pages]);

  return {
    items,
    total: result?.total ?? items.length,
    hasMore: result?.hasMore ?? lastHasMore,
  };
}
